import React from 'react';
import {
    ContactState,
    ProfileViewMode,
} from '../../types/profile/profileTypes';
import Card from '../../muiComponents/Card';
import ContactDetail from './ContactDetail';
import useUserProfile from '../../customHooks/useUserProfile';
import { useAccountInformation } from '../../contexts/AccountInformationContext';
import { Avatar } from '@mui/material';
import useTranslate from '../../customHooks/useTranslate';
import Button from '../../muiComponents/Button';
import { useNotification } from '../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../utils/commonVariables/messages';

interface ContactCardProps {
    contact: ContactState;
}

const ContactCard = ({ contact }: ContactCardProps) => {
    const translate = useTranslate();
    const { readOnlyUser } = useUserProfile();
    const { handleViewModeChange, handleDetailContact } =
        useAccountInformation();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const avatarInitials = contact.Name.split(' ')
        .map(word => word.charAt(0))
        .join('');

    const handleCopyEmail = async () => {
        try {
            await navigator.clipboard.writeText(contact.Email);
            setSuccessMessage(FeedbackMessages.COPIED);
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        }
    };

    const handleEdit = () => {
        handleDetailContact(contact);
        handleViewModeChange(ProfileViewMode.EDIT);
    };

    return (
        <Card className='contact-card'>
            <header className='contact-card__header'>
                <Avatar className='contact-card__avatar'>
                    {avatarInitials}
                </Avatar>
                <div className='flex-column flex-grow'>
                    <h2 className='contact-card__name'>{contact.Name}</h2>
                    <p className='contact-card__position'>{contact.Position}</p>
                </div>
            </header>
            <article className='contact-card__content'>
                {contact.ContactType && (
                    <ContactDetail
                        iconName='Category'
                        text={contact.ContactType}
                    />
                )}
                {contact.Email && (
                    <ContactDetail
                        iconName='EmailOutlined'
                        text={contact.Email}
                    />
                )}
                {contact.Phone && (
                    <ContactDetail
                        iconName='PhoneAndroidOutlined'
                        text={contact.Phone}
                    />
                )}
                {contact.ResponseTime && (
                    <ContactDetail
                        iconName='AccessTime'
                        text={
                            translate('response_time') +
                            ': ' +
                            contact.ResponseTime
                        }
                    />
                )}
                {contact.Notes && (
                    <ContactDetail
                        iconName='StickyNote2Outlined'
                        text={contact.Notes}
                        truncate={false}
                    />
                )}
            </article>
            <footer className='contact-card__footer'>
                <div className='flex gap-10'>
                    <Button
                        startIcon={'ContentCopy'}
                        variant='outlined'
                        color='secondary'
                        onClick={handleCopyEmail}
                        disabled={!contact.Email}
                    >
                        {translate('copy_email')}
                    </Button>
                    {!readOnlyUser && (
                        <Button
                            startIcon={'Edit'}
                            variant='outlined'
                            color='secondary'
                            onClick={handleEdit}
                        >
                            {translate('edit')}
                        </Button>
                    )}
                </div>
            </footer>
        </Card>
    );
};

export default ContactCard;
